import { IdIcon } from '@assets/icons/id';
import { ProfileIcon } from '@assets/icons/profile';
import { ErrorMessage, TaInput } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import Chip from '@mui/material/Chip/Chip';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateUser } from 'services/users';
import { User, useUsersStore } from 'services/users/usersStore';
import * as Yup from 'yup';
import { EditableCard } from '../editableCard/editableCard';

const initialValues = {
	company_id: '',
	fullName: '',
};

const companyInfoValidationSchema = Yup.object({
	company_id: Yup.string().required('fieldNameError'),
	fullName: Yup.string().required('fieldNameError'),
});

export function CompanyInfo({
	readonly = false,
	user: userProp,
}: {
	readonly?: boolean;
	user?: User | null;
}) {
	const { user: storeUser } = useUsersStore();

	// Use provided user prop or fallback to store user

	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const mutate = useUpdateUser();

	const user = userProp || storeUser;

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: companyInfoValidationSchema,
		onSubmit: async (values) => {
			const nameParts = values.fullName.split(' ');
			const formattedValue = {
				first_name: nameParts[0],
				last_name: nameParts.slice(1).join(' '),
				companies: [
					{
						company_id: values.company_id,
						company_name: user?.company.company_name || '',
					},
				],
			};

			setIsLoading(true);
			try {
				await mutate(formattedValue);
				setOpen(false);
				setSnackbarOpen(true);
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		setValues({
			company_id: user?.company.company_id || '',
			fullName: user?.first_name + ' ' + user?.last_name || '',
		});
	}, [user?.company.company_id, user?.first_name, user?.last_name, setValues]);

	return (
		<EditableCard
			onEdit={() => setOpen(true)}
			className="responsive-card"
			title={t('companyInfo')}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex gap-[10px]">
					<IdIcon />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">
						{user?.company.company_id}
					</p>
					<Chip
						label={t('private')}
						variant="outlined"
						sx={{
							borderRadius: '5px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 5px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '15px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
				<div className="flex gap-[10px]">
					<ProfileIcon />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">
						{user?.first_name} {user?.last_name}
					</p>
					<Chip
						label={t('private')}
						variant="outlined"
						sx={{
							borderRadius: '5px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 5px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '15px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				onClose={() => setOpen(false)}
				title={t('contactDetails')}
				showActions
			>
				<div className="flex flex-col gap-[10px] mt-[20px]">
					<div className="flex flex-col">
						<span className="roboto-regular text-[16px] leading-[140%] mb-[10px]">
							{t('companyIdNumberIs')}
						</span>
						<TaInput
							icon={<IdIcon />}
							id="company_id"
							label={t('idNumber')}
							size="small"
							isRequired
							value={formik.values.company_id}
							onChange={formik.handleChange}
							error={formik.touched.company_id && Boolean(formik.errors.company_id)}
						/>
						{formik.touched.company_id && formik.errors.company_id && (
							<ErrorMessage
								message={t(formik.errors.company_id || '', {
									fieldName: t('idNumber'),
								})}
							/>
						)}
					</div>
					<div className="flex flex-col">
						<span className="roboto-regular text-[16px] leading-[140%] mb-[10px]">
							{t('companyRepresentativesNameIs')}
						</span>
						<TaInput
							icon={<ProfileIcon />}
							id="fullName"
							label={t('representativeFullName')}
							size="small"
							isRequired
							value={formik.values.fullName}
							onChange={formik.handleChange}
							error={formik.touched.fullName && Boolean(formik.errors.fullName)}
						/>
						{formik.touched.fullName && formik.errors.fullName && (
							<ErrorMessage
								message={t(formik.errors.fullName || '', {
									fieldName: t('fullName'),
								})}
							/>
						)}
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('contactDetails'),
				})}
			/>
		</EditableCard>
	);
}
